import { useEffect } from "react";
import { useFirstMountState } from "react-use";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGetReadingHistory } from "@pages/MyAccount/hooks/useGetReadingHistory";
import { GaObject } from "@pages/MyAccount/utils/types";
import useOKTAUserStore from "@store/useOKTAUserStore";

import MyAccountRecentReadPopoverEmptyState from "./MyAccountReadingHistoryPopoverEmptyState";
import MyAccountRecentReadPopoverList from "./MyAccountReadingHistoryPopoverList";

/**
 * MyAccountReadingHistoryPopover Component - Popover for recent reading history
 */
const MyAccountReadingHistoryPopover = (): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const userLoginId = OKTAUserInfo?.mysphw;

  const sendDataToGTM = useGTMDispatch();
  const isFirstMount = useFirstMountState();

  const { readArticleList, isLoading } = useGetReadingHistory({
    pageSize: 6,
  });

  const isEmptyState = readArticleList.length === 0;
  const isDisplayViewMore = readArticleList.length > 5;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isFirstMount) return;

    const gaObject: GaObject = {
      event: "custom_event",
      eventCategory: "recent read",
      eventAction: "visible",
      eventLabel: window.location.href,
    };

    sendDataToGTM(gaObject);
  }, [sendDataToGTM, isFirstMount]);

  return (
    <div
      data-testid="my-account-reading-history-popover-component"
      className="w-[360px] p-6"
    >
      {!userLoginId || (isEmptyState && !isLoading) ? (
        <MyAccountRecentReadPopoverEmptyState />
      ) : null}

      {!isEmptyState && userLoginId && !isLoading ? (
        <MyAccountRecentReadPopoverList
          articleList={readArticleList.slice(0, 5)}
          isDisplayViewMore={isDisplayViewMore}
        />
      ) : null}

      {isLoading && userLoginId ? (
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} size="2x" spin className="mb-6" />
        </div>
      ) : null}
    </div>
  );
};

export default MyAccountReadingHistoryPopover;
